import {
    LoadingScreen,
    useAlert,
    useAuth,
    useForm,
    useMerchantConfig,
} from "@venuepos/react-common";
import {
    useProductCreateMutation,
    useProductFormQuery,
    useIsProductPluInUseLazyQuery,
} from "graphql-sdk";
import { defaultProduct, IProductInput, schemaProduct } from "lib";
import React, { useCallback, useRef } from "react";
import { useTranslation } from "locales";
import { useHandleMutationError } from "../../../hooks/use-handle-mutation-error";
import { RootStackScreenProps } from "../../../navigation";
import { ProductScreen } from "../product-screen";
import { useFocusEffect } from "@react-navigation/native";
import { ProductForm } from "../form";

type ScreenProps = RootStackScreenProps<"PRODUCT_EDIT">;

export function ProductCreateScreen({ navigation: { navigate } }: ScreenProps) {
    const auth = useAuth();
    auth.enforce("merchant.product");

    const hasFocusedOnce = useRef(false);
    const { currency } = useMerchantConfig();
    const [t] = useTranslation();
    const [productCreate] = useProductCreateMutation();
    const { handleMutationError } = useHandleMutationError();
    const alert = useAlert();

    const {
        data: formData,
        loading: formLoading,
        refetch,
    } = useProductFormQuery({
        fetchPolicy: "cache-and-network",
    });

    const form = useForm<IProductInput>(schemaProduct, defaultProduct);
    const [{ values }, { reset }] = form;

    const [checkPlu] = useIsProductPluInUseLazyQuery();

    const create = useCallback(async () => {
        if (!values) {
            return;
        }

        if (values.plu) {
            let isPluInUse;
            try {
                isPluInUse = await checkPlu({
                    variables: { plu: values.plu },
                });
            } catch (error) {
                await handleMutationError(async () => {
                    throw error;
                });
                return;
            }

            if (isPluInUse.data?.isProductPluInUse.status) {
                await alert(
                    t(
                        "backoffice.product.duplicate_plu",
                        "Duplicate PLU found"
                    ),
                    t(
                        "backoffice.product.plu_in_use",
                        "PLU {{plu}} is already in use. Please enter a different PLU or leave it blank.",
                        { plu: values.plu }
                    )
                );
                return;
            }
        }

        await handleMutationError(
            async () =>
                await productCreate({
                    variables: {
                        product: {
                            parentId: values.parentId,
                            groupId: values.groupId,
                            externalId: values.externalId,
                            name: values.name,
                            amount: values.amount,
                            minimumAmount: values.minimumAmount,
                            costAmount: values.costAmount,
                            ownVatId: values.ownVatId || null,
                            onlineRequired: values.onlineRequired,
                            active: values.active,
                            isExternal: values.isExternal,
                            ownExcludeFromDiscounts:
                                values.ownExcludeFromDiscounts,
                            barcode: values.barcode,
                            barcodes: values.barcodes,
                            buttonText: values.buttonText,
                            printerId: values.printerId || null,
                            inheritPrinter: values.inheritPrinter,
                            plu: values.plu || null,
                            discountTemplate: values.discountTemplate || [],
                        },
                    },
                }),
            t("backoffice.product.created", "Product created"),
            () => {
                reset();
                navigate("PRODUCTS");
            }
        );
    }, [
        alert,
        checkPlu,
        handleMutationError,
        navigate,
        productCreate,
        reset,
        t,
        values,
    ]);

    useFocusEffect(
        useCallback(() => {
            if (hasFocusedOnce.current) {
                refetch();
            } else {
                hasFocusedOnce.current = true;
            }
        }, [refetch])
    );

    if (formLoading) {
        return <LoadingScreen />;
    }

    return (
        <ProductScreen>
            <ProductForm
                form={form}
                printers={formData?.printers.data}
                virtualPrinters={formData?.virtualPrinters.data}
                vats={formData?.vats.data}
                productGroups={formData?.productGroups.data}
                products={formData?.products.data}
                onSubmit={create}
                currency={currency}
                submitButton={["common.create", "Create"]}
            />
        </ProductScreen>
    );
}
